import {
  ADDON_CONTENTS,
  DOCUMENTS,
  LEVEL_CONTENTS,
  briefWindow,
  longDay,
  moodLabel,
  oneThing,
  shiftDay,
  signedFigure,
  type SharedView as SharedPayload,
} from '@adhd/shared';
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { RecordSheet, type SheetRecord } from '../components/RecordSheet.js';
import { PrinterIcon } from '../components/Icons.js';
import { api, ApiError } from '../lib/api.js';
import { bandOf } from '../lib/band.js';
import { asRows, dayCell, dayIn, num, sosPathName, str, toReport, toSheetRecord, type WeeklyRecap } from './SharedView.js';

/**
 * The two documents a link's page offers. Release 5b of 2.0 (docs/PRD-2.0.md §7).
 *
 * A SHAPE, NOT A GRANT. Both are rendered from the same payload the
 * recipient's page already reads — `GET /shared/:token`, built by the same
 * function, through the same partner rule — so neither can show a word the
 * page does not. What they add is an order, a masthead, and a sheet of paper.
 *
 * The session brief, for a therapist link: the stretch since the last
 * appointment in the app's voice, the days, four figures, the homework as he
 * wrote it, the worksheets in the template as it is, the threads, and one
 * question computed from the figures and never written freehand.
 *
 * The week, for a partner link: the reading that level already gets, as one
 * page, with the seven days and what helps.
 */

/** The public route: /shared/:token/brief or /week. Fetches the same view; the access log names the document. */
export function SharedDocumentPage(): JSX.Element {
  const { token, doc } = useParams<{ token: string; doc: string }>();
  const kind = doc === 'brief' || doc === 'week' || doc === 'card' ? doc : null;
  const [view, setView] = useState<SharedPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setView(null);
    setError(null);
    if (!token || !kind) {
      setError('That link is not valid.');
      return;
    }
    api
      .sharedView(token, kind)
      .then(setView)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Can't reach the server right now."));
  }, [token, kind]);

  return (
    <main className="app stack-lg stack" style={{ paddingBottom: 'var(--space-8)' }}>
      {error ? (
        <div className="banner banner-error">{error}</div>
      ) : !view || !kind ? (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '50dvh' }}>
          <div className="spinner" aria-label="Loading" />
        </div>
      ) : (
        <>
          <p className="no-print">
            <Link className="linkish" to={`/shared/${token}`}>
              ‹ The full page
            </Link>
          </p>
          <SharedDocument view={view} kind={kind} pageHref={`/shared/${token}/page`} />
        </>
      )}
    </main>
  );
}

export function SharedDocument({ view, kind, pageHref }: { view: SharedPayload; kind: 'brief' | 'week' | 'card'; pageHref?: string }): JSX.Element {
  return kind === 'brief' ? <SessionBrief view={view} pageHref={pageHref} /> : kind === 'week' ? <WeekDoc view={view} pageHref={pageHref} /> : <FriendCard view={view} pageHref={pageHref} />;
}

/** The owner's line for this reader, on any document, right under the masthead. */
function FromOwner({ view }: { view: SharedPayload }): JSX.Element | null {
  if (!view.note) return null;
  return (
    <>
      <h3>From {view.ownerDisplayName}</h3>
      <p className="doc-own">{view.note}</p>
    </>
  );
}

/* --- Shared pieces --------------------------------------------------------- */

/** "F", "S", … for the row under a strip. */
const initial = (day: string): string => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'narrow', timeZone: 'UTC' });
/** "Fri 9". */
const shortDay = (day: string): string => `${new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' })} ${Number(day.slice(8))}`;
const weekdayOf = (day: string): string => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'UTC' });
const isMonday = (day: string): boolean => new Date(`${day}T12:00:00Z`).getUTCDay() === 1;
/** The fonts ship a real minus; signedFigure uses a hyphen. On paper the difference shows. */
const minus = (s: string): string => s.replace('-', '−');
/** "2:10 pm" in the owner's zone. */
function clockIn(iso: string, timezone: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: timezone }).replace(/\s*(am|pm)$/i, ' $1');
}
/** Where a day ended: the last check-in, else the day's mean, rounded. */
function endOf(g: Record<string, unknown>): number | null {
  const e = num(g, 'lastMood') ?? num(g, 'meanMood');
  return e === null ? null : Math.round(e);
}

/* --- The paper ----------------------------------------------------------- */

export function DocShell({
  mast,
  foot,
  children,
  pageHref,
  kind,
}: {
  mast: [string, string];
  foot: string;
  children: ReactNode;
  /** The whole page, behind the document (the fixes pass). */
  pageHref?: string;
  /** Sets the paper's class, for the week's and the card's larger print sizes. */
  kind?: 'brief' | 'week' | 'card' | 'month';
}): JSX.Element {
  return (
    <div className="stack-sm stack">
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary btn-compact" onClick={() => window.print()}>
          <PrinterIcon /> Print or save
        </button>
      </div>
      <article className={`doc${kind ? ` doc-${kind}` : ''}`}>
        <div className="doc-mast">
          <span>{mast[0]}</span>
          <span>{mast[1]}</span>
        </div>
        {children}
        <div className="doc-foot">{foot}</div>
      </article>
      {pageHref && (
        <p className="no-print doc-offer">
          <Link className="linkish" to={pageHref}>
            The whole page, every section this link allows ›
          </Link>
        </p>
      )}
    </div>
  );
}

/**
 * One cell per day, coloured by where the day ended; dashed where it was not
 * rated. The documents study (15 Oct 2026): a weekday letter under every cell,
 * Mondays dated, today outlined, and — where `figures` is on — the signed
 * figure inside each rated cell, so a photocopy still reads. The hover and the
 * screen-reader text carry the word and the weekday, never a count out of a
 * total.
 */
export function DocStrip({
  days,
  figures = false,
  labels = true,
  today = null,
}: {
  days: { day: string; end: number | null }[];
  figures?: boolean;
  labels?: boolean;
  today?: string | null;
}): JSX.Element {
  const words = days.filter((d) => d.end !== null).map((d) => `${shortDay(d.day)} ${moodLabel(d.end as number)}`).join(', ');
  return (
    <>
      <div className="doc-strip" role="img" aria-label={words ? `Where each day ended: ${words}` : 'No day rated'}>
        {days.map((d) => (
          <i
            key={d.day}
            className={`${d.end === null ? 'is-empty' : `band-${bandOf(d.end)}`}${d.day === today ? ' is-today' : ''}`}
            title={`${shortDay(d.day)}: ${d.end === null ? 'not rated' : moodLabel(d.end)}`}
          >
            {figures && d.end !== null ? <b>{minus(signedFigure(d.end))}</b> : null}
          </i>
        ))}
      </div>
      {labels && (
        <div className="doc-strip-days" aria-hidden="true">
          {days.map((d) => (
            <span key={d.day}>
              {initial(d.day)}
              {isMonday(d.day) || d.day.endsWith('-01') ? <em>{Number(d.day.slice(8))}</em> : null}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

export function Figures({ items }: { items: { value: string; label: string }[] }): JSX.Element {
  return (
    <div className="doc-figs">
      {items.map((f) => (
        <div className="doc-fig" key={f.label}>
          <b>{f.value}</b>
          <span>{f.label}</span>
        </div>
      ))}
    </div>
  );
}

/** Markdown-ish paragraphs: a ### line becomes a chapter heading; the rest are paragraphs. */
export function Prose({ text }: { text: string }): JSX.Element {
  const parts = text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <>
      {parts.map((p, i) =>
        /^#{1,6}\s/.test(p) ? (
          <h3 key={i}>{p.replace(/^#{1,6}\s+/, '')}</h3>
        ) : (
          <p key={i} className={i === 0 ? 'doc-lead' : undefined}>
            {p.replace(/^\s*[-*]\s+/, '• ')}
          </p>
        ),
      )}
    </>
  );
}

function range(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = shiftDay(d, 1)) out.push(d);
  return out;
}

/** The level and add-on names this link does not hold, for the brief's last line. */
function leavesOut(view: SharedPayload): string[] {
  const g = new Set(view.grants ?? []);
  const levels = Object.values(LEVEL_CONTENTS).filter((l) => !l.grants.every((x) => g.has(x))).map((l) => l.name);
  const addons = Object.values(ADDON_CONTENTS).filter((a) => !a.grants.every((x) => g.has(x))).map((a) => a.name);
  return [...levels, ...addons];
}

/** The newest day with a rating, for "Now" on the week and the word on the card. */
function newestRated(glances: { day: string; end: number | null }[]): { day: string; end: number } | null {
  for (let i = glances.length - 1; i >= 0; i--) {
    const g = glances[i]!;
    if (g.end !== null) return { day: g.day, end: g.end };
  }
  return null;
}

/* --- The session brief --------------------------------------------------- */

/**
 * Page one as a clinician would ask it (the documents study, 15 Oct 2026, all
 * eight decisions his): what is going on first, then the days as one
 * labelled figure with the lowest moment in its caption, then the app's line
 * for each day that has one, then what he did about it — worksheets as index
 * lines, Help now as rows, the homework as written — then medication when
 * granted, one question from the fixed rules citing its block, and what this
 * link leaves out, computed. On paper the worksheets are the appendix: each in
 * the unchanged template on its own page after the footer. A shape over the
 * link's own page, never a grant.
 */
export function SessionBrief({ view, pageHref }: { view: SharedPayload; pageHref?: string }): JSX.Element {
  const { data, ownerDisplayName: owner, label } = view;
  const tz = view.ownerTimezone;
  const today = dayIn(view.generatedAt, tz) ?? view.generatedAt.slice(0, 10);
  const { from, to } = briefWindow(today, view.sessionOn, view.lastSessionOn);
  const days = range(from, to);
  const has = (k: string): boolean => Object.prototype.hasOwnProperty.call(data, k);

  // Where each day ended, and whether anything was written on it: the
  // day-by-day table where the link has it, the week behind today otherwise.
  const ends = new Map<string, { end: number | null; written: boolean }>();
  for (const r of asRows(data.trends)) {
    const day = str(r, 'day');
    if (!day) continue;
    const end = num(r, 'last_mood') ?? num(r, 'mean_mood');
    ends.set(day, { end: end === null ? null : Math.round(end), written: (num(r, 'voice_logs') ?? 0) > 0 || (num(r, 'thought_records') ?? 0) > 0 });
  }
  const glances = asRows(data.dailyRecap)
    .map((g) => ({ day: str(g, 'day') ?? '', end: endOf(g), headline: str(g, 'headline'), kind: str(g, 'lineKind'), written: (num(g, 'journalEntries') ?? 0) > 0 || (num(g, 'thoughtRecords') ?? 0) > 0 }))
    .filter((g) => g.day)
    .sort((a, b) => a.day.localeCompare(b.day));
  for (const g of glances) if (!ends.has(g.day)) ends.set(g.day, { end: g.end, written: g.written });
  const strip = days.map((day) => ({ day, end: ends.get(day)?.end ?? null }));
  const rated = strip.filter((d): d is { day: string; end: number } => d.end !== null);
  const lowest = rated.length ? rated.reduce((a, b) => (b.end < a.end ? b : a)) : null;
  const highest = rated.length ? rated.reduce((a, b) => (b.end > a.end ? b : a)) : null;

  // The lowest single rating with its hour — sharper than the ending, and on
  // the page already (decision 4).
  const moments = asRows(data.mood)
    .map((r) => ({ day: str(r, 'day') ?? '', at: str(r, 'recorded_at') ?? str(r, 'recordedAt'), mood: num(r, 'mood') }))
    .filter((m): m is { day: string; at: string; mood: number } => Boolean(m.day) && m.at !== null && m.mood !== null && m.day >= from && m.day <= to);
  const lowestMoment = moments.length ? moments.reduce((a, b) => (b.mood < a.mood ? b : a)) : null;

  const worksheets: SheetRecord[] = asRows(data.thoughtRecords)
    .map(toSheetRecord)
    .filter((w) => w.occurredAt.slice(0, 10) >= from && w.occurredAt.slice(0, 10) <= to)
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));

  const helpNow = asRows(data.sosEvents)
    .map((r) => ({ day: str(r, 'day') ?? '', at: str(r, 'started_at'), before: num(r, 'distress_before'), after: num(r, 'distress_after'), path: str(r, 'path_taken') }))
    .filter((h) => h.day >= from && h.day <= to)
    .sort((a, b) => (a.at ?? a.day).localeCompare(b.at ?? b.day));

  const homework = asRows(data.homework).map((h) => {
    const all = asRows(h.answers).map((a) => ({
      day: String(a.day ?? ''),
      said: asRows(a.said).map((l) => ({ label: String(l.label ?? ''), text: String(l.text ?? '') })),
    }));
    return {
      title: String(h.title ?? ''),
      startedOn: str(h, 'startedOn'),
      endsOn: str(h, 'endsOn'),
      lastAnswerDay: all.length ? all[all.length - 1]!.day : null,
      answers: all.filter((a) => a.day >= from && a.day <= to),
    };
  });

  const readings: WeeklyRecap[] = asRows(data.reports)
    .map(toReport)
    .filter((r) => r.summary !== null && r.weekEnd >= from && r.weekStart <= to)
    .sort((a, b) => a.weekStart.localeCompare(b.weekStart));

  const threads = asRows(data.lifeEvents).map((e) => ({ label: str(e, 'label') ?? 'Something big', since: str(e, 'startedOn') }));

  const meds = asRows(data.medications).map((m) => ({ name: str(m, 'name') ?? '', dose: num(m, 'dose_amount'), unit: str(m, 'dose_unit') ?? '', active: (num(m, 'is_active') ?? 1) === 1 }));
  const adherence = asRows(data.adherence)
    .map((r) => ({ day: str(r, 'day') ?? '', taken: num(r, 'meds_taken') ?? 0, assumed: num(r, 'meds_assumed') ?? 0, scheduled: num(r, 'meds_scheduled') ?? 0, missed: num(r, 'meds_missed') ?? 0, skipped: num(r, 'meds_skipped') ?? 0 }))
    .filter((r) => r.day >= from && r.day <= to);
  const dosesTaken = adherence.reduce((n, r) => n + r.taken, 0);
  const dosesScheduled = adherence.reduce((n, r) => n + r.scheduled, 0);
  const dosesAssumed = adherence.reduce((n, r) => n + r.assumed, 0);
  const missedDays = adherence.filter((r) => r.missed > 0 || r.skipped > 0).map((r) => shortDay(r.day));

  // "Written" for the one-thing rule counts a homework answer and a Help now
  // moment on that day too (the clinician's defect 7).
  const writtenOn = new Set<string>([
    ...[...ends.entries()].filter(([, v]) => v.written).map(([d]) => d),
    ...homework.flatMap((h) => h.answers.map((a) => a.day)),
    ...helpNow.map((h) => h.day),
  ]);
  const question = oneThing({
    today,
    worksheets: worksheets.map((w) => ({ day: w.occurredAt.slice(0, 10), beliefBefore: w.beliefBefore, beliefAfter: w.beliefAfter, isComplete: w.isComplete })),
    lowestDay: lowest ? { day: lowest.day, written: writtenOn.has(lowest.day) } : null,
    homework: homework.map((h) => ({ title: h.title, lastAnswerDay: h.lastAnswerDay, endsOn: h.endsOn })),
  });
  // The question points at the block it came from.
  const cites = ((): string | null => {
    if (!question) return null;
    if (question.id === 'belief_held' || question.id === 'unfinished') {
      const day = question.text.match(/of (\w+ \d+ \w+)/)?.[1];
      const idx = worksheets.findIndex((w) => day && longDay(w.occurredAt.slice(0, 10)) === day);
      return idx >= 0 ? `Sheet ${idx + 1}, appended.` : null;
    }
    if (question.id === 'lowest_unwritten') return 'Figure 1.';
    if (question.id === 'homework_stopped') return 'The homework, above.';
    return null;
  })();

  const heading = view.sessionOn && view.sessionOn >= today ? `Before ${longDay(view.sessionOn)}` : `Since ${longDay(from)}`;
  const out = leavesOut(view);
  const span = days.length;
  const lineKind = glances.find((g) => g.headline && g.kind)?.kind ?? null;

  // Numbered as they appear; a block the link does not grant is absent and
  // named in the last line, so the numbers are assigned here, not fixed.
  const blocks: { title: string; body: JSX.Element }[] = [];
  if (has('lifeEvents')) {
    blocks.push({
      title: 'Going on',
      body: threads.length ? (
        <p>{threads.map((t) => `${t.label}${t.since ? `, since ${longDay(t.since)}` : ''}`).join(' · ')}.</p>
      ) : (
        <p className="doc-muted">Nothing marked as running.</p>
      ),
    });
  }
  blocks.push({
    title: 'The days',
    body: (
      <div className="doc-figure">
        <DocStrip days={strip} figures today={today} />
        <p className="doc-cap">
          <b>Figure 1.</b> Where each day ended, {dayCell(from)} – {dayCell(to)}: {rated.length} of {span} rated.
          {lowest && highest ? ` Lowest ending ${minus(signedFigure(lowest.end))} on ${weekdayOf(lowest.day)} ${Number(lowest.day.slice(8))}; highest ${minus(signedFigure(highest.end))} on ${weekdayOf(highest.day)} ${Number(highest.day.slice(8))}.` : ''}
          {lowestMoment ? ` Lowest single rating ${minus(signedFigure(lowestMoment.mood))}, ${weekdayOf(lowestMoment.day)} ${Number(lowestMoment.day.slice(8))} at ${clockIn(lowestMoment.at, tz)}.` : ''}
          {' '}Counted from the check-ins.
        </p>
      </div>
    ),
  });
  const lines = glances.filter((g) => g.headline && g.day >= from && g.day <= to);
  if (lines.length > 0) {
    blocks.push({
      title: 'What the days held',
      body: (
        <>
          <p className="doc-muted">
            The app's line for each of the last seven days where one was written — {lineKind === 'held' ? `what ${owner}'s day held` : `how ${owner} seemed`}. The app's words, not {owner}'s.
          </p>
          <div className="doc-rows">
            {lines.map((g) => (
              <Fragment key={g.day}>
                <span className="k">{shortDay(g.day)}</span>
                <span>{g.headline}</span>
              </Fragment>
            ))}
          </div>
        </>
      ),
    });
  }
  if (has('thoughtRecords') || has('helpNow') || has('homework')) {
    blocks.push({
      title: 'What he did about it',
      body: (
        <div className="doc-rows">
          {has('thoughtRecords') && (
            <>
              <span className="k">Worksheets</span>
              <span>
                {worksheets.length === 0 ? (
                  <span className="doc-muted">None in this stretch.</span>
                ) : (
                  worksheets.map((w, i) => (
                    <span key={`${w.occurredAt}-${i}`} style={{ display: 'block' }}>
                      <span className="q">“{w.automaticThought}”</span>{' '}
                      <span className="n">
                        {shortDay(w.occurredAt.slice(0, 10))} · {w.isComplete && w.beliefBefore !== null && w.beliefAfter !== null ? `${w.beliefBefore}% → ${w.beliefAfter}%` : 'not finished'} · sheet {i + 1}
                      </span>
                    </span>
                  ))
                )}
              </span>
            </>
          )}
          {has('helpNow') && (
            <>
              <span className="k">Help now</span>
              <span>
                {helpNow.length === 0 ? (
                  <span className="doc-muted">None in this stretch.</span>
                ) : (
                  helpNow.map((h, i) => (
                    <span key={`${h.at ?? h.day}-${i}`} className="n" style={{ display: 'block' }}>
                      {shortDay(h.day)}{h.at ? `, ${clockIn(h.at, tz)}` : ''}{h.before !== null ? ` · ${h.before} → ${h.after ?? '—'}` : ''}{h.path ? ` · ${sosPathName(h.path)}` : ''}
                    </span>
                  ))
                )}
              </span>
            </>
          )}
          {has('homework') && (
            <>
              <span className="k">Homework</span>
              <span>
                {homework.length === 0 ? (
                  <span className="doc-muted">None set.</span>
                ) : (
                  homework.map((h) => (
                    <span key={h.title} style={{ display: 'block' }}>
                      <span className="n">“{h.title}”{h.startedOn && h.endsOn ? ` · set for ${dayCell(h.startedOn)} – ${dayCell(h.endsOn)}` : ''}</span>
                      {h.answers.length === 0 ? (
                        <span className="doc-muted" style={{ display: 'block' }}>Nothing written for it in this stretch.</span>
                      ) : (
                        h.answers.map((a) => (
                          <span key={a.day} className="doc-answer" style={{ display: 'block' }}>
                            <span className="doc-when">{longDay(a.day)}</span>
                            {a.said.map((l, i) => (
                              <p key={i} className="doc-own">{a.said.length > 1 && <span className="doc-when">{l.label} · </span>}{l.text}</p>
                            ))}
                          </span>
                        ))
                      )}
                    </span>
                  ))
                )}
              </span>
            </>
          )}
        </div>
      ),
    });
  }
  if (has('meds')) {
    blocks.push({
      title: 'Medication',
      body: (
        <div className="doc-rows">
          <span className="k">Takes</span>
          <span>{meds.filter((m) => m.active).map((m) => `${m.name}${m.dose !== null ? ` ${m.dose} ${m.unit}` : ''}`).join(' · ') || 'Nothing listed.'}{meds.some((m) => !m.active) ? ` Stopped: ${meds.filter((m) => !m.active).map((m) => m.name).join(', ')}.` : ''}</span>
          <span className="k">{dayCell(from)} – {dayCell(to)}</span>
          <span>
            {dosesScheduled === 0
              ? 'No dose was scheduled in this stretch.'
              : `${dosesTaken} of ${dosesScheduled} scheduled doses counted as taken${dosesAssumed > 0 ? `, ${dosesAssumed} of them assumed because nothing was marked` : ''}${missedDays.length ? `; missed or skipped on ${missedDays.join(', ')}` : ''}. A dose is counted unless ${owner} says otherwise.`}
          </span>
        </div>
      ),
    });
  }

  const titles = blocks.map((b, i) => `${i + 1} ${b.title}`);

  // IN NINETY SECONDS — back at the top at Nick's word ("i like the 90 second
  // version"). Counted from the record, never written: the word most days
  // ended on, the lowest and highest, the lowest moment, what he did about it,
  // what is running. Then the finished week's reading where there is one, as
  // the app's. The written version of this — a cover for the reader — waits
  // on the voice study (docs/recipient-study-2026-10-13.md §5).
  const modeWord = ((): string | null => {
    if (rated.length === 0) return null;
    const counts = new Map<string, number>();
    for (const d of rated) counts.set(moodLabel(d.end), (counts.get(moodLabel(d.end)) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]![0];
  })();
  const finishedSheets = worksheets.filter((w) => w.isComplete);
  const movedSheets = finishedSheets.filter((w) => w.beliefBefore !== null && w.beliefAfter !== null && w.beliefAfter < w.beliefBefore);
  const lead = [
    rated.length === 0
      ? `Nothing rated in these ${span} days.`
      : `${rated.length} of ${span} days rated${modeWord ? `; most ended ${modeWord}` : ''}.`,
    lowest && highest && lowest.day !== highest.day
      ? `Lowest ending ${minus(signedFigure(lowest.end))} on ${weekdayOf(lowest.day)} ${Number(lowest.day.slice(8))}, highest ${minus(signedFigure(highest.end))} on ${weekdayOf(highest.day)} ${Number(highest.day.slice(8))}${lowestMoment && lowestMoment.mood < (lowest?.end ?? 0) ? `; the lowest moment ${minus(signedFigure(lowestMoment.mood))}, ${weekdayOf(lowestMoment.day)} ${Number(lowestMoment.day.slice(8))} at ${clockIn(lowestMoment.at, tz)}` : ''}.`
      : null,
    has('thoughtRecords')
      ? worksheets.length === 0
        ? 'No worksheet in this stretch.'
        : `${worksheets.length === 1 ? 'One worksheet' : `${worksheets.length} worksheets`}, ${finishedSheets.length === worksheets.length ? (worksheets.length === 1 ? 'finished' : 'all finished') : `${finishedSheets.length} finished`}${movedSheets.length > 0 ? `; the belief moved on ${movedSheets.length === finishedSheets.length ? (movedSheets.length === 1 ? 'it' : 'each') : `${movedSheets.length} of them`}` : finishedSheets.length > 0 ? '; the belief did not move' : ''}.`
      : null,
    has('helpNow') ? (helpNow.length === 0 ? 'No Help now moment.' : `Help now ${helpNow.length === 1 ? 'once' : `${helpNow.length} times`}, ${[...new Set(helpNow.map((h) => `${weekdayOf(h.day)} ${Number(h.day.slice(8))}`))].join(' and ')}.`) : null,
    has('lifeEvents') && threads.length > 0 ? `Running: ${threads.map((t) => t.label).join(', ')}.` : null,
  ].filter((x): x is string => x !== null).join(' ');

  return (
    <DocShell
      kind="brief"
      mast={[`${owner} · for ${label} · ${DOCUMENTS.brief.name.toLowerCase()}`, `${dayCell(from)} – ${dayCell(to)}`]}
      foot={`Read-only · revocable by ${owner} · each opening logged`}
      pageHref={pageHref}
    >
      <h2>{span === 14 ? 'Two weeks' : `${span} days`} of a daily record</h2>
      <p className="doc-when">{heading} · {span} days · {rated.length} rated</p>
      <FromOwner view={view} />
      <h3>In ninety seconds</h3>
      <p className="doc-lead">{lead}</p>
      {readings.map((r) => (
        <div key={r.weekStart} className="stack-sm stack">
          <p>{r.summary![0]}</p>
          <p className="doc-muted">The app's reading of the week of {longDay(r.weekStart)}, written once it was over; the rest of it is appended.</p>
        </div>
      ))}
      <p className="doc-muted">Counted from the record, never written — except the week's reading, which is the app's and says so.</p>
      {worksheets.length > 0 && (
        <p className="doc-contents">
          {titles.map((t) => <span key={t}>{t}</span>)}
          <span>If you ask one thing</span>
          <span>{worksheets.length === 1 ? 'Sheet 1' : `Sheets 1–${worksheets.length}`}, appended</span>
        </p>
      )}

      {blocks.map((b, i) => (
        <Fragment key={b.title}>
          <h3>{i + 1} · {b.title}</h3>
          {b.body}
        </Fragment>
      ))}

      <div className="doc-end">
        <h3>If you ask one thing</h3>
        <p className="doc-lead">{question ? question.text : 'Nothing in the figures stood out for this stretch.'}</p>
        <p className="doc-muted">Chosen by a fixed rule from the figures above; never written freehand.{cites ? ` ${cites}` : ''}</p>
        <h3>What this leaves out</h3>
        <p className="doc-muted">
          {[...out, ...(has('helpNow') ? [] : ['Help now moments']), 'journal entries', 'day marks', 'people'].join(', ')} — not on this link. The whole page, every section this link allows, is under this document.
        </p>
      </div>

      {(readings.length > 0 || worksheets.length > 0) && (
        <div className="doc-sheets">
          {readings.length > 0 && (
            <div className="stack-sm stack doc-sheet">
              <h3>The weeks, as the app wrote them</h3>
              <p className="doc-muted">Written once each week was over, for this link's level; not for {label}.</p>
              {readings.map((r) => (
                <div key={r.weekStart} className="stack-sm stack">
                  <span className="doc-when">{dayCell(r.weekStart)} – {dayCell(r.weekEnd)}</span>
                  {r.summary!.map((p, i) => <p key={i} className={i === 0 ? 'doc-lead' : undefined}>{p}</p>)}
                </div>
              ))}
            </div>
          )}
          {worksheets.map((w, i) => (
            <div key={`${w.occurredAt}-${i}`} className="stack-sm stack doc-sheet">
              <h3>Sheet {i + 1} · {longDay(w.occurredAt.slice(0, 10))}</h3>
              {/* The template as it is, on screen and on paper: protected, on its own stage. */}
              <div className="doc-stage"><RecordSheet record={w} timezone={tz} /></div>
            </div>
          ))}
        </div>
      )}
    </DocShell>
  );
}

/* --- The week ------------------------------------------------------------ */

/**
 * For a partner link (the documents study, 15 Oct 2026): now, the last seven
 * days, then the finished week's reading over its own seven days, the thread
 * by name only because the link holds it, and his words. No mean, no count,
 * no word for a day he never rated. When the reading is not on this link, one
 * sentence says so and names no reason.
 */
export function WeekDoc({ view, pageHref }: { view: SharedPayload; pageHref?: string }): JSX.Element {
  const { data, ownerDisplayName: owner, label } = view;
  const reports = asRows(data.reports).map(toReport);
  const latest = reports[0] ?? null;
  const weekEnds = asRows(data.reports)[0]?.ends;
  const ownDays = Array.isArray(weekEnds)
    ? (weekEnds as Record<string, unknown>[]).map((e) => ({ day: str(e, 'day') ?? '', end: num(e, 'end') })).filter((e) => e.day)
    : null;
  const glances = asRows(data.dailyRecap)
    .map((g) => ({ day: str(g, 'day') ?? '', end: endOf(g) }))
    .filter((g) => g.day)
    .sort((a, b) => a.day.localeCompare(b.day));
  const today = dayIn(view.generatedAt, view.ownerTimezone);
  const now = newestRated(glances);
  const kind = str(data as Record<string, unknown>, 'weeklyKind');
  const threads = asRows(data.lifeEvents).map((e) => ({ label: str(e, 'label') ?? 'Something big', since: str(e, 'startedOn') }));

  return (
    <DocShell
      kind="week"
      mast={[`${owner} · for ${label} · ${DOCUMENTS.week.name.toLowerCase()}`, today ? `to ${longDay(today)}` : '']}
      foot={`Read-only · how ${owner} seemed, never what happened · revocable by ${owner} · each opening logged`}
      pageHref={pageHref}
    >
      <h2>The week</h2>
      <FromOwner view={view} />
      <h3>Now</h3>
      {now ? (
        <p className="doc-lead">
          <span className={`doc-swatch band-${bandOf(now.end)}`} aria-hidden="true" />
          {moodLabel(now.end)}, {now.day === today ? 'where today stands so far' : `where ${weekdayOf(now.day)} ended`}.
        </p>
      ) : (
        <p className="doc-lead doc-muted">Nothing rated in the last seven days.</p>
      )}
      {glances.length > 0 && (
        <>
          <h3>The last seven days</h3>
          <DocStrip days={glances} figures today={today} />
          <p className="doc-cap">Where each day ended. Dashed: no check-in that day. Today is outlined.</p>
        </>
      )}
      <h3>{latest ? `The week of ${longDay(latest.weekStart)}` : 'The finished week'}</h3>
      {latest === null || latest.summary === null ? (
        <p className="doc-lead doc-muted">No reading of {latest ? 'this week' : 'a finished week'} on this link.</p>
      ) : (
        latest.summary.map((p, i) => (
          <p key={i} className={i === 0 ? 'doc-lead' : undefined}>{p}</p>
        ))
      )}
      {ownDays && ownDays.length > 0 && (
        <>
          <DocStrip days={ownDays} figures />
          <p className="doc-cap">Where each day of that week ended.</p>
        </>
      )}
      {latest && latest.summary !== null && (
        <p className="doc-muted">Written by the app once the week was over, {kind === 'held' ? 'as what the week held' : 'as how the week seemed'}, for this link's level.</p>
      )}
      {Object.prototype.hasOwnProperty.call(data, 'lifeEvents') && threads.length > 0 && (
        <>
          <h3>Running</h3>
          <p className="doc-muted">{threads.map((t) => `${t.label}${t.since ? `, since ${longDay(t.since)}` : ''}`).join(' · ')}.</p>
        </>
      )}
      {view.whatHelps && (
        <>
          <h3>What helps</h3>
          <p className="doc-own">{view.whatHelps}</p>
          <p className="doc-muted">In {owner}'s words, written for you; nothing the app wrote.</p>
        </>
      )}
    </DocShell>
  );
}

/* --- The friend's card ------------------------------------------------------ */

/**
 * For a friend link (the documents study, 15 Oct 2026): the word with its day
 * in the same breath, his line, seven cells with letters under them and no
 * figures in them, what helps. Nothing written by a model, no thread, no
 * count. When fewer than half the seven days are rated, no word is promoted.
 */
export function FriendCard({ view, pageHref }: { view: SharedPayload; pageHref?: string }): JSX.Element {
  const { data, ownerDisplayName: owner, label } = view;
  const glances = asRows(data.dailyRecap)
    .map((g) => ({ day: str(g, 'day') ?? '', end: endOf(g) }))
    .filter((g) => g.day)
    .sort((a, b) => a.day.localeCompare(b.day));
  const today = dayIn(view.generatedAt, view.ownerTimezone);
  const ratedCount = glances.filter((g) => g.end !== null).length;
  const thin = glances.length > 0 && ratedCount < Math.ceil(glances.length / 2);
  const latest = newestRated(glances);
  return (
    <DocShell
      kind="card"
      mast={[`${owner} · for ${label} · ${DOCUMENTS.card.name.toLowerCase()}`, today ? `to ${longDay(today)}` : '']}
      foot={`Read-only · how ${owner} seems, never what happened · revocable by ${owner} · each opening logged`}
      pageHref={pageHref}
    >
      {latest && !thin ? (
        <>
          <h2>
            <span className={`doc-swatch band-${bandOf(latest.end)}`} aria-hidden="true" />
            {moodLabel(latest.end)}
          </h2>
          <p className="doc-when">{latest.day === today ? 'where today stands, so far' : `where ${weekdayOf(latest.day)} ended · nothing yet today`}</p>
        </>
      ) : (
        <>
          <h2>{glances.length === 0 ? `How ${owner} seems` : 'Not much rated this week'}</h2>
          <p className="doc-when">{glances.length === 0 ? 'nothing rated yet' : `${ratedCount} of the last ${glances.length} days rated`}</p>
        </>
      )}
      <FromOwner view={view} />
      {glances.length > 0 && (
        <>
          <h3>The last seven days</h3>
          <DocStrip days={glances} today={today} />
          <p className="doc-cap">Where each day ended. Dashed: no check-in that day.</p>
        </>
      )}
      {view.whatHelps && (
        <>
          <h3>What helps</h3>
          <p className="doc-own">{view.whatHelps}</p>
        </>
      )}
    </DocShell>
  );
}
